import { RootStore } from "./RootStore";
import { makeAutoObservable, runInAction } from "mobx";
import type { LoadStatus } from "./loadStatus";
import * as Sentry from "@sentry/react";
import { inflateQuestionsPayload } from "@/utils/inflateQuestionsPayload";
import * as fns from "@/api/fetch";
import { SUBSECTION_TO_SECTION_ID } from "./TestsStore";
import { PRACTICE_TYPE_DATA, PracticeTestType } from "../../appsConfig";
import {
  BACKUP_STATE_INFO,
  BACKUP_CURRICULUM,
  BACKUP_TYPES_MIN_MAX,
} from "./randomizationConstants/offlineBackups";
import assetsForApp from "../../appsAssetListing";
import { StorageKeys } from "@/utils/storage";
import i18n, {
  DEFAULT_LANGUAGE,
  getCurrentLanguage,
  type AnswerKey,
  type LocalizedStringMap,
  type SupportedLanguage,
} from "@/i18n";
import { localizePracticeTypeData } from "@/i18n/practiceTypeCopy";
import type { Question } from "@petergok/blue-collar-shared";

const ANSWER_KEYS: AnswerKey[] = [
  "answer_1",
  "answer_2",
  "answer_3",
  "answer_4",
];

type LocalizableQuestionField = "question" | "hint" | "walkthrough" | AnswerKey;
type LocalizedQuestionFieldKey = `${LocalizableQuestionField}_i18n`;

// Permissive shape: web reads questions from Supabase as untyped objects with
// runtime fields (e.g. `diagram` may be a URL string even though the shared
// Question interface marks it boolean). Keep the index signature so callsites
// that read other fields type-check, while still surfacing the i18n shape.
export type QuestionWithI18n = Partial<Question> &
  Partial<Record<LocalizedQuestionFieldKey, LocalizedStringMap>> & {
    [key: string]: any;
  };

function resolveI18nField(
  question: QuestionWithI18n,
  field: LocalizableQuestionField,
  language: SupportedLanguage,
): string {
  if (language === DEFAULT_LANGUAGE) {
    return (question[field] as string) || "";
  }
  const i18nField = question[`${field}_i18n` as LocalizedQuestionFieldKey];
  return (
    i18nField?.[language] ||
    i18nField?.[DEFAULT_LANGUAGE] ||
    (question[field] as string) ||
    ""
  );
}

function deriveCorrectAnswerKey(
  question: QuestionWithI18n | null,
): AnswerKey | null {
  if (!question) return null;

  const directKey = question.correct_answer_key as AnswerKey | undefined;
  if (directKey && ANSWER_KEYS.includes(directKey)) {
    return directKey;
  }

  const correctAnswer = question.correct_answer as string | undefined;
  if (correctAnswer && ANSWER_KEYS.includes(correctAnswer as AnswerKey)) {
    return correctAnswer as AnswerKey;
  }

  if (correctAnswer) {
    const matchingKeys = ANSWER_KEYS.filter(
      (key) => question[key] === correctAnswer,
    );
    if (matchingKeys.length === 1) {
      return matchingKeys[0];
    }
  }

  // Ambiguous: multiple slots share the correct text (legacy duplicate-answer
  // data) or no slot matches. Caller falls back to per-slot text equality.
  return null;
}

const warnedMissingKeyIds = new Set<string>();

let saveFrequenciesTimer: ReturnType<typeof setTimeout> | null = null;

export interface StateInfo {
  codebookYear: Record<string, number>;
  stateDescriptions: Record<string, string>;
  stateExamNames: Record<string, string>;
  stateNames: Record<string, string>;
  totalExamQuestions: Record<string, number>;
  examPassingScore: Record<string, number>;
  examTimeAllowed: Record<string, number>;
}

export type CurriculumValue = number | "GUARANTEED_ALWAYS" | "GUARANTEED_NONE";
export interface Curriculum {
  [sectionNum: string]: {
    per_of_q: number | Record<string, number>;
    codebook: CurriculumValue | Record<string, CurriculumValue>;
    diagram: CurriculumValue | Record<string, CurriculumValue>;
    subsection_splits: Record<string, { per_of_q: number }>;
  };
}
export interface TypesMinMax {
  [type: string]: {
    subsection_id: number;
    min_q: number;
    max_q: number | "unlimited";
  };
}

export default class QuestionsStore {
  rootStore: RootStore;

  // observables
  questions = new Map<string, any>();
  deletedQuestions = new Map<string, any>();
  questionUseFrequency = new Map<string, number>();
  isLoading = false;
  loadingError: any = null;
  /**
   * Whether the RETIRED-question set has been fetched.
   *
   * This is load-bearing for exam history: `Test.allQuestionsLoaded()` requires every
   * question id to be in either `questions` or `deletedQuestions`, so until this lands,
   * any past exam containing a retired question is silently excluded. Nothing in the
   * splash gate waits on this fetch — `isLoading` tracks only the active bank — so a
   * failure here is invisible unless callers check this flag.
   */
  deletedQuestionsStatus: LoadStatus = "idle";

  stateInfo: StateInfo = BACKUP_STATE_INFO;
  curriculum: Curriculum = BACKUP_CURRICULUM;
  typesMinMax: TypesMinMax = BACKUP_TYPES_MIN_MAX;

  freshFromFirebase = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.loadQuestions();
    this.loadQuestionFrequencies();
    makeAutoObservable(this, { rootStore: false });

    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", () => {
        if (saveFrequenciesTimer) {
          clearTimeout(saveFrequenciesTimer);
          saveFrequenciesTimer = null;
          // keepalive lets the POST survive the page unload; without it most
          // browsers cancel the in-flight fetch as the tab tears down.
          this._flushQuestionFrequencies({ keepalive: true });
        }
      });
    }
  }

  getQuestionById = (questionId: string): QuestionWithI18n | null => {
    return (
      this.questions.get(questionId) ||
      this.deletedQuestions.get(questionId) ||
      null
    );
  };

  getCorrectAnswerKey = (
    question: QuestionWithI18n | null,
  ): AnswerKey | null => {
    if (!question) return null;

    const answerKey = deriveCorrectAnswerKey(question);

    if (!answerKey && question?.question_id) {
      const id = question.question_id as string;
      if (!warnedMissingKeyIds.has(id)) {
        warnedMissingKeyIds.add(id);
        console.warn(
          `[i18n] Missing correct_answer_key for question ${id} (warn-once)`,
        );
      }
    }

    return answerKey;
  };

  isAnswerCorrect = (
    question: QuestionWithI18n | null,
    selectedAnswerSlot?: number | null,
  ): boolean => {
    if (
      !question ||
      !selectedAnswerSlot ||
      selectedAnswerSlot < 1 ||
      selectedAnswerSlot > 4
    ) {
      return false;
    }

    const correctAnswerKey = this.getCorrectAnswerKey(question);
    if (correctAnswerKey) {
      return correctAnswerKey === `answer_${selectedAnswerSlot}`;
    }

    // Ambiguous-slot fallback (legacy data with duplicate answer text):
    // any slot whose text equals correct_answer counts as correct.
    const selectedKey = `answer_${selectedAnswerSlot}` as AnswerKey;
    const selectedText = question[selectedKey] as string | undefined;
    return (
      !!question.correct_answer &&
      !!selectedText &&
      selectedText === question.correct_answer
    );
  };

  getLocalizedQuestion = (
    questionId: string,
    language: SupportedLanguage = getCurrentLanguage(),
  ): QuestionWithI18n | null => {
    const question = this.getQuestionById(questionId);
    if (!question) return null;

    const correctAnswerKey = this.getCorrectAnswerKey(question);
    const localized: QuestionWithI18n = {
      ...question,
      question: resolveI18nField(question, "question", language),
      hint: resolveI18nField(question, "hint", language),
      walkthrough: resolveI18nField(question, "walkthrough", language),
      answer_1: resolveI18nField(question, "answer_1", language),
      answer_2: resolveI18nField(question, "answer_2", language),
      answer_3: resolveI18nField(question, "answer_3", language),
      answer_4: resolveI18nField(question, "answer_4", language),
    };

    if (correctAnswerKey) {
      localized.correct_answer_key = correctAnswerKey;
      localized.correct_answer =
        (localized[correctAnswerKey] as string) || question.correct_answer;
    } else {
      // Ambiguous (duplicate-text legacy data) — drop a possibly-dirty key and
      // localize correct_answer so the per-slot text-equality fallback in
      // isAnswerCorrect compares Spanish-vs-Spanish (or whatever the active
      // language is). Without this, every slot scores wrong on render even
      // though Test.numCorrect (which uses raw English) is right.
      delete localized.correct_answer_key;
      const matchingSlot = ANSWER_KEYS.find(
        (key) => (question[key] as string | undefined) === question.correct_answer,
      );
      if (matchingSlot) {
        localized.correct_answer =
          (localized[matchingSlot] as string) || question.correct_answer;
      }
    }

    return localized;
  };

  async reset() {
    this.questions = new Map<string, any>();
    this.deletedQuestions = new Map<string, any>();
    this.deletedQuestionsStatus = "idle";
    this.questionUseFrequency = new Map<string, number>();
    this.isLoading = false;
    this.loadingError = null;

    this.stateInfo = BACKUP_STATE_INFO;
    this.curriculum = BACKUP_CURRICULUM;
    this.typesMinMax = BACKUP_TYPES_MIN_MAX;

    this.freshFromFirebase = false;

    this.loadQuestions();
    this.loadQuestionFrequencies();

    return;
  }

  /**
   * Retries the question-bank fetch after a failure. Wired to the full-screen failure
   * gate in App.tsx — without a retry the only recovery is a manual page reload.
   */
  retryLoadQuestions = () => {
    runInAction(() => {
      this.loadingError = null;
    });
    this.loadQuestions();
  };

  /** Load questions from Firestore */
  loadQuestions() {
    this.isLoading = true;
    this.deletedQuestionsStatus = "loading";

    // Firestore fetch
    fns
      .getAllQuestions()
      .then((data: any) => {
        // decompress if needed (shared decoder — see inflateQuestionsPayload)
        let decodeError: unknown = null;
        try {
          data = inflateQuestionsPayload(data);
        } catch (err) {
          console.error("Failed to decompress questions_compressed:", err);
          Sentry.captureException(err);
          decodeError = err;
        }

        const newMap = new Map<string, any>();
        if (!decodeError) {
          Object.keys(data).forEach((qid) => {
            const q = data[qid];
            q.id = qid;
            q.section_id = SUBSECTION_TO_SECTION_ID[Number(q.subsection_id)];
            if (q.subsection_id) {
              newMap.set(qid, q);
            }
          });
        }

        runInAction(() => {
          if (newMap.size) {
            this.questions = newMap;
            this.freshFromFirebase = true;
            this.loadingError = null;
          } else {
            // An empty bank is a FAILURE, not a valid state — the app cannot function
            // without questions. Record it so the splash can show an error.
            this.loadingError =
              decodeError ?? new Error("Question bank loaded empty");
          }
          // ALWAYS clear the flag. This used to live inside `if (newMap.size)`, so a
          // decode failure or an empty payload left `isLoading` true forever and
          // App.tsx rendered <SplashScreen/> permanently, with no error and no retry.
          // The decoder now THROWS on an unrecognised shape (it used to silently yield
          // nothing), which makes that path easier to reach — so this must not depend
          // on the happy path succeeding.
          this.isLoading = false;
        });
      })
      .catch((e) => {
        runInAction(() => {
          this.loadingError = e;
          this.isLoading = false;
        });
        Sentry.captureException(e);
      });

    fns
      .getDeletedQuestions()
      .then((data: any) => {
        // Uses the same shared decoder as the active bank. This path previously
        // had its own copy that dereferenced a misspelled `questions_compressedc`
        // and handled only the Buffer shape, so `deletedQuestions` was never
        // populated and past exams containing retired questions disappeared.
        try {
          data = inflateQuestionsPayload(data);
        } catch (err) {
          console.error("Failed to decompress questions_compressed:", err);
          Sentry.captureException(err);
          runInAction(() => {
            this.deletedQuestionsStatus = "error";
          });
          return;
        }

        const newMap = new Map<string, any>();
        Object.keys(data).forEach((qid) => {
          const q = data[qid];
          q.id = qid;
          q.section_id = SUBSECTION_TO_SECTION_ID[Number(q.subsection_id)];
          if (q.subsection_id) {
            newMap.set(qid, q);
          }
        });

        runInAction(() => {
          if (newMap.size) {
            this.deletedQuestions = newMap;
          }
          // An empty retired-question set is legitimate (nothing has been retired yet),
          // unlike the active bank above.
          this.deletedQuestionsStatus = "loaded";
        });
      })
      .catch((e) => {
        Sentry.captureException(e);
        runInAction(() => {
          this.deletedQuestionsStatus = "error";
        });
      });

    if (PracticeTestType.byState) {
      fns
        .getStateInfo()
        .then((snapshot: any) => {
          const raw = snapshot?.value;
          if (raw) {
            try {
              const info = JSON.parse(raw);
              if (Object.keys(info).length) {
                this.stateInfo = info;
              }
            } catch (e) {
              Sentry.captureException(e);
            }
          }
        })
        .catch((e) => Sentry.captureException(e));
    }

    fns
      .getCurriculum()
      .then((snapshot: any) => {
        const raw = snapshot?.value;
        if (raw) {
          try {
            const info = JSON.parse(raw);
            if (Object.keys(info).length) {
              this.curriculum = info;
            }
          } catch (e) {
            Sentry.captureException(e);
          }
        }
      })
      .catch((e) => Sentry.captureException(e));

    fns
      .getTypesMinMax()
      .then((snapshot: any) => {
        const raw = snapshot?.value;
        if (raw) {
          try {
            const info = JSON.parse(raw);
            if (Object.keys(info).length) {
              this.typesMinMax = info;
            }
          } catch (e) {
            Sentry.captureException(e);
          }
        }
      })
      .catch((e) => Sentry.captureException(e));
  }

  /** Load and sync question-use frequencies */
  loadQuestionFrequencies() {
    try {
      const raw = localStorage.getItem(StorageKeys.QUESTION_USE_FREQUENCY);
      if (raw) {
        const parsed: [string, number][] = JSON.parse(raw);
        const map = new Map(parsed);
        if (map.size) {
          this.questionUseFrequency = map;
        }
      }
    } catch (e) {
      console.warn("Error parsing QUESTION_USE_FREQUENCY", e);
    }

    if (this.questionUseFrequency.size === 0) {
      fns
        .getQuestionFrequencies()
        .then((snapshot) => {
          const latest = snapshot?.latest;
          if (latest) {
            try {
              const parsed: [string, number][] = JSON.parse(latest);
              const map = new Map(parsed);
              if (map.size) {
                this.questionUseFrequency = map;
                this.saveQuestionFrequencies();
              }
            } catch (e) {
              Sentry.captureException(e);
            }
          }
        })
        .catch((e) => Sentry.captureException(e));
    }
  }

  /** Persist frequencies locally & to Firestore (debounced; flushed on unload). */
  saveQuestionFrequencies() {
    if (saveFrequenciesTimer) clearTimeout(saveFrequenciesTimer);
    saveFrequenciesTimer = setTimeout(() => {
      saveFrequenciesTimer = null;
      this._flushQuestionFrequencies();
    }, 3000);
  }

  private async _flushQuestionFrequencies(opts?: { keepalive?: boolean }) {
    const entries = Array.from(this.questionUseFrequency.entries());
    const str = JSON.stringify(entries);
    try {
      localStorage.setItem(StorageKeys.QUESTION_USE_FREQUENCY, str);
    } catch (e) {
      Sentry.captureException(e);
    }
    try {
      await fns.setQuestionFrequencies(str, opts);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  /** Increment use count & schedule save */
  addUsedQuestions(questionIds: string[]) {
    questionIds.forEach((id) => {
      const count = this.questionUseFrequency.get(id) || 0;
      this.questionUseFrequency.set(id, count + 1);
    });
    this.saveQuestionFrequencies();
  }

  getExamTypeTitle(state?: string) {
    if (PracticeTestType.byState && state) {
      // i18n: i18n key takes the state's exam name as a parameter so the
      // " Exam" / " Examen" suffix translates with the rest of the UI.
      return i18n.t("study.exam_with_name", {
        examName: this.stateInfo.stateExamNames[state],
      });
    }
    const localized = localizePracticeTypeData(
      PracticeTestType.examType,
      PRACTICE_TYPE_DATA[PracticeTestType.examType] || {},
    );
    return localized.title || "";
  }

  getExamTypeMainDescription(state?: string) {
    if (PracticeTestType.byState && state) {
      const total = this.getTotalExamTimeMinutes(state);
      const hrs = Math.floor(total / 60);
      const mins = total % 60;
      const desc = i18n.t("study.exam_duration", {
        hours: hrs,
        minutes: mins,
      });
      const baseDescription =
        localizePracticeTypeData(
          PracticeTestType.examType,
          PRACTICE_TYPE_DATA[PracticeTestType.examType] || {},
        ).mainDescription || "";
      return baseDescription
        .replace("{exam_time}", desc)
        .replace("{exam_name}", this.stateInfo.stateExamNames[state]);
    }
    return (
      localizePracticeTypeData(
        PracticeTestType.examType,
        PRACTICE_TYPE_DATA[PracticeTestType.examType] || {},
      ).mainDescription || ""
    );
  }

  getStatesOrdered() {
    const names = this.stateInfo.stateNames;
    return Object.keys(names).sort((a, b) => {
      const aOther = a.startsWith("O_");
      const bOther = b.startsWith("O_");
      if (aOther !== bOther) return aOther ? 1 : -1;
      return names[a].localeCompare(names[b]);
    });
  }

  getStateDropdownItems() {
    return this.getStatesOrdered().map((state) => ({
      key: state,
      name: this.stateInfo.stateNames[state],
    }));
  }

  getAvailableCodebookYears(): number[] {
    if (!this.stateInfo?.codebookYear) return [];
    const years = [...new Set(Object.values(this.stateInfo.codebookYear))];
    return years.sort((a, b) => b - a);
  }

  getEffectiveCodebookYear(stateCode: string): number | undefined {
    const override = this.rootStore.mainAppStore.selectedCodebookYear;
    if (override != null) return override;
    return this.stateInfo?.codebookYear?.[stateCode];
  }

  getNumExamTypeQuestions(state?: string) {
    if (!state || !PracticeTestType.byState) {
      return PracticeTestType.numExamTypeQuestions;
    }
    return this.stateInfo.totalExamQuestions[state];
  }

  getTotalExamTimeMinutes(state?: string) {
    if (!state || !PracticeTestType.byState) {
      return PracticeTestType.totalExamTimeMinutes;
    }
    return this.stateInfo.examTimeAllowed[state];
  }

  getPassingScore(state?: string) {
    if (!state || !PracticeTestType.byState) {
      return PracticeTestType.passingScore;
    }
    return this.stateInfo.examPassingScore[state];
  }

  getSecondsPerQuestion(state?: string) {
    const total = this.getTotalExamTimeMinutes(state);
    const count = this.getNumExamTypeQuestions(state);
    return Math.round((total * 60) / count);
  }

  getResultListTopText(state?: string) {
    if (PracticeTestType.byState && state) {
      const secs = this.getSecondsPerQuestion(state);
      const mins = Math.floor(secs / 60);
      const rem = secs % 60;
      const avg = rem
        ? i18n.t("common.duration_minutes_seconds_short", {
            minutes: mins,
            seconds: rem,
          })
        : i18n.t("common.duration_minutes_short", { minutes: mins });
      return assetsForApp.resultList.result_list_top_text
        .replace(
          "{pass_score_perc}",
          `${Math.round(this.stateInfo.examPassingScore[state] * 100)}%`
        )
        .replace("{required_avg_time}", avg);
    }
    return assetsForApp.resultList.result_list_top_text;
  }
}
